import { useCallback, useEffect, useRef, useState } from 'react';
import { wpApi } from '../../../api/wpApi';
import { faiconApi } from '../../../api/faiconApi';
import {
  extractWordPressChildEventDates,
  extractFaiconSessionDates,
  getExternalEventDisplayTitle,
} from '../utils/dates';
import {
  getCachedDays,
  getCachedEvents,
  setCachedDays,
  setCachedEvents,
} from '../utils/catalogCache';

const normalizeFaiconEvents = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const normalizeId = (value) => (value ? String(value).trim() : '');

const withDisplayTitles = (events) => (
  (events || []).map((event) => ({
    ...event,
    displayTitle: event.displayTitle || getExternalEventDisplayTitle(event),
  }))
);

async function fetchEventsForSource(source) {
  if (source === 'WordPress') {
    const wpEvents = await wpApi.getEventsWithChildren();
    return withDisplayTitles(wpEvents);
  }

  if (source === 'Faicon') {
    const faiconResponse = await faiconApi.getAllEvents();
    const faiconEvents = normalizeFaiconEvents(faiconResponse);
    return withDisplayTitles(faiconEvents.map((event) => ({
      id: String(event.id),
      title: event.name || event.title,
      sessions: event.sessions || [],
    })));
  }

  return [];
}

async function fetchDaysForSource(source, eventId) {
  if (source === 'WordPress') {
    const childEvents = await wpApi.getChildEvents(eventId);
    return extractWordPressChildEventDates(childEvents);
  }

  if (source === 'Faicon') {
    const faiconResponse = await faiconApi.getSessionsByEvent(eventId);
    const faiconSessions = faiconResponse?.results || faiconResponse?.data || faiconResponse || [];
    return extractFaiconSessionDates(faiconSessions);
  }

  return [];
}

export function useExternalSourceCatalog(source, { linkedEventId = '', linkedSessionId = '' } = {}) {
  const [events, setEvents] = useState(() => getCachedEvents(source) || []);
  const [days, setDays] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingDays, setLoadingDays] = useState(false);
  const [error, setError] = useState(null);

  const daysRequestRef = useRef(0);
  const prevLinkedRef = useRef({ eventId: '', sessionId: '' });

  const normalizedLinkedEventId = normalizeId(linkedEventId);
  const normalizedLinkedSessionId = normalizeId(linkedSessionId);
  const linkedSessionIdRef = useRef(normalizedLinkedSessionId);
  linkedSessionIdRef.current = normalizedLinkedSessionId;
  const sessionValueKey = source === 'WordPress' ? 'ID' : 'id';

  const resetSelection = useCallback(() => {
    setSelectedEvent(null);
    setSelectedDay(null);
    setSelectedSession(null);
    setDays([]);
    setSessions([]);
  }, []);

  const findEventInList = useCallback((eventList, eventId) => {
    if (!eventId) return null;
    return eventList.find((event) => String(event.id) === eventId) || null;
  }, []);

  const findSessionInDays = useCallback((daysList, sessionId) => {
    if (!sessionId) return null;
    for (const day of daysList) {
      const match = (day.sessions || []).find(
        (session) => String(session[sessionValueKey] ?? session.id ?? '') === sessionId
      );
      if (match) {
        return { day, session: match };
      }
    }
    return null;
  }, [sessionValueKey]);

  const reloadEvents = useCallback(async () => {
    if (!source || source === 'Custom') return;

    setLoadingEvents(true);
    setError(null);

    try {
      const eventsData = await fetchEventsForSource(source);
      setCachedEvents(source, eventsData);
      setEvents(eventsData);
    } catch (err) {
      setEvents([]);
      setCachedEvents(source, null);
      setError(err.message || `Failed to load events from ${source}`);
    } finally {
      setLoadingEvents(false);
    }
  }, [source]);

  useEffect(() => {
    if (!source || source === 'Custom') {
      setEvents([]);
      resetSelection();
      return;
    }

    const cached = getCachedEvents(source);
    if (cached) {
      setEvents(withDisplayTitles(cached));
      setLoadingEvents(false);
      return;
    }

    let cancelled = false;
    setLoadingEvents(true);
    setError(null);

    fetchEventsForSource(source)
      .then((eventsData) => {
        if (cancelled) return;
        setCachedEvents(source, eventsData);
        setEvents(eventsData);
      })
      .catch((err) => {
        if (cancelled) return;
        setEvents([]);
        setCachedEvents(source, null);
        setError(err.message || `Failed to load events from ${source}`);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingEvents(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [source, resetSelection]);

  useEffect(() => {
    if (!events.length || loadingEvents) return;

    const linkedChanged = (
      prevLinkedRef.current.eventId !== normalizedLinkedEventId
      || prevLinkedRef.current.sessionId !== normalizedLinkedSessionId
    );
    if (!linkedChanged) {
      return;
    }

    prevLinkedRef.current = {
      eventId: normalizedLinkedEventId,
      sessionId: normalizedLinkedSessionId,
    };

    if (!normalizedLinkedEventId && !normalizedLinkedSessionId) {
      setSelectedEvent(null);
      setSelectedDay(null);
      setSelectedSession(null);
      return;
    }

    const matchedEvent = findEventInList(events, normalizedLinkedEventId);
    setSelectedEvent(matchedEvent);
    if (!matchedEvent) {
      setSelectedDay(null);
      setSelectedSession(null);
    }
  }, [
    events,
    loadingEvents,
    normalizedLinkedEventId,
    normalizedLinkedSessionId,
    findEventInList,
  ]);

  useEffect(() => {
    if (!selectedEvent?.id || !source || source === 'Custom') {
      setDays([]);
      setSelectedDay(null);
      setSelectedSession(null);
      setSessions([]);
      return;
    }

    const eventId = selectedEvent.id;
    const cachedDays = getCachedDays(source, eventId);
    if (cachedDays) {
      setDays(cachedDays);
      return;
    }

    const requestId = daysRequestRef.current + 1;
    daysRequestRef.current = requestId;
    let cancelled = false;

    setLoadingDays(true);
    setError(null);

    fetchDaysForSource(source, eventId)
      .then((daysData) => {
        if (cancelled || daysRequestRef.current !== requestId) return;
        setCachedDays(source, eventId, daysData);
        setDays(daysData);
      })
      .catch((err) => {
        if (cancelled || daysRequestRef.current !== requestId) return;
        setDays([]);
        setError(err.message || 'Failed to load days/sessions');
      })
      .finally(() => {
        if (!cancelled && daysRequestRef.current === requestId) {
          setLoadingDays(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEvent?.id, source]);

  useEffect(() => {
    if (!days.length) return;

    const sessionId = linkedSessionIdRef.current;
    if (!sessionId) return;

    const matched = findSessionInDays(days, sessionId);
    if (!matched) return;

    setSelectedDay((prev) => (
      prev?.date === matched.day.date ? prev : matched.day
    ));
    setSelectedSession((prev) => {
      const prevId = String(prev?.[sessionValueKey] ?? prev?.id ?? '');
      const nextId = String(matched.session[sessionValueKey] ?? matched.session.id ?? '');
      return prevId === nextId ? prev : matched.session;
    });
  }, [days, normalizedLinkedSessionId, findSessionInDays, sessionValueKey]);

  useEffect(() => {
    if (!selectedDay) {
      setSessions([]);
      return;
    }
    setSessions(selectedDay.sessions || []);
  }, [selectedDay]);

  return {
    events,
    days,
    sessions,
    selectedEvent,
    setSelectedEvent,
    selectedDay,
    setSelectedDay,
    selectedSession,
    setSelectedSession,
    loadingEvents,
    loadingDays,
    error,
    loadEvents: reloadEvents,
    resetSelection,
  };
}
